import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { api } from "../api";
import { DataTable } from "../components/DataTable";
import type { Ingredient } from "../types";

export function IngredientList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["ingredients", search],
    queryFn: () => api.listIngredients({ search: search || undefined, per_page: 200 }),
  });

  const createMutation = useMutation({
    mutationFn: (d: Partial<Ingredient>) => api.createIngredient(d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ingredients"] });
      setShowForm(false);
    },
  });

  const columns = [
    { key: "name", label: "原料名稱", sortable: true, filterable: true, render: (i: Ingredient) => (
      <span className="font-medium text-slate-900">{i.name}</span>
    )},
    { key: "category", label: "分類", sortable: true, filterable: true },
    { key: "unit", label: "單位", sortable: true, width: "5rem" },
    { key: "supplier_name", label: "供應商", sortable: true, filterable: true, render: (i: Ingredient) => (
      <span className="text-xs text-slate-500">{i.supplier_name ?? "-"}</span>
    )},
  ];

  return (
    <div className="max-w-5xl space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">原料</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          <Plus size={16} /> 新增
        </button>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          className="input pl-9"
          placeholder="搜尋原料..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {showForm && (
        <form
          className="rounded-xl border border-slate-200 bg-white p-5 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            createMutation.mutate({
              name: fd.get("name") as string,
              category: (fd.get("category") as string) || undefined,
              unit: fd.get("unit") as string,
              supplier_id: fd.get("supplier_id") ? Number(fd.get("supplier_id")) : undefined,
            });
          }}
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <input name="name" className="input" placeholder="原料名稱 *" required />
            <input name="category" className="input" placeholder="分類" />
            <input name="unit" className="input" placeholder="單位 (g/ml/顆) *" required />
            <input name="supplier_id" className="input" placeholder="供應商 ID" type="number" />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
              儲存
            </button>
            <button type="button" className="btn" onClick={() => setShowForm(false)}>
              取消
            </button>
          </div>
        </form>
      )}

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        exportable
        exportFilename="原料列表"
        emptyMessage="沒有原料資料"
      />
    </div>
  );
}
